import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CreateIcon from '@mui/icons-material/Create';
import PublishIcon from '@mui/icons-material/Publish';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { showError } from '../../utils/errorHandler';
import { useTheme } from '@mui/material/styles';
import { bg } from '../../utils/darkModeAdapter';

const MediaInquiryPage = ({ plan, onClose }) => {
  const theme = useTheme();
  // 簡易メールバリデータ
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [allSelected, setAllSelected] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPrivacyDialog, setOpenPrivacyDialog] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  // axios設定
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // プランに含まれるメディアリスト
  // 複数のパターンからメディアリストを取得
  const mediaList = React.useMemo(() => {
    // 複数のパターンでメディアリストを探す
    const list = plan?.recommendedMedia || 
                 plan?.mediaList || 
                 plan?.planA?.mediaList ||
                 plan?.planB?.mediaList ||
                 (plan?.gptAdvice?.planA?.mediaList) ||
                 (plan?.gptAdvice?.planB?.mediaList) ||
                 [
                   { id: 1, name: 'マイナビ新卒', cost: '120万〜200万' },
                   { id: 2, name: 'リクナビ', cost: '50万〜120万' },
                   { id: 3, name: 'キャリタス就活', cost: '30万〜80万' },
                   { id: 4, name: 'ONE CAREER', cost: '100万〜' }
                 ];
    
    // 配列の場合のみ返し、各アイテムが適切な構造を持っているかチェック
    if (Array.isArray(list)) {
      const processedList = list.map((item, index) => {
        // IDを適切に設定 - 文字列の場合は数値に変換を試みる
        let mediaId = item?.id;
        if (typeof mediaId === 'string' && mediaId.startsWith('fallback-')) {
          mediaId = index + 1; // fallbackの場合は連番を使用
        } else if (!mediaId) {
          mediaId = index + 1; // IDがない場合も連番を使用
        }
        
        return {
          id: mediaId,
          name: item?.name || `未知の媒体 ${index + 1}`,
          cost: item?.cost || '未設定'
        };
      });
      return processedList;
    }
    
    const fallbackList = [
      { id: 1, name: 'マイナビ新卒', cost: '120万〜200万' },
      { id: 2, name: 'リクナビ', cost: '50万〜120万' },
      { id: 3, name: 'キャリタス就活', cost: '30万〜80万' },
      { id: 4, name: 'ONE CAREER', cost: '100万〜' }
    ];
    return fallbackList;
  }, [plan]);

  // コンポーネントのマウント時に全メディアを選択
  React.useEffect(() => {
    if (mediaList && mediaList.length > 0) {
      setSelectedMedia(mediaList.map(media => media.id));
      setAllSelected(true);
    }
  }, [mediaList]);

  // 全選択/解除の処理
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedMedia(mediaList.map(media => media.id));
      setAllSelected(true);
    } else {
      setSelectedMedia([]);
      setAllSelected(false);
    }
  };

  // 個別選択/解除の処理
  const handleSelectMedia = (id) => {
    const currentIndex = selectedMedia.indexOf(id);
    const newSelected = [...selectedMedia];

    if (currentIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedMedia(newSelected);
    setAllSelected(newSelected.length === mediaList.length);
  };

  // プライバシーポリシーダイアログを開く
  const handleOpenPrivacyDialog = () => {
    if (selectedMedia.length === 0) {
      alert('少なくとも1つのメディアを選択してください');
      return;
    }
    setOpenPrivacyDialog(true);
  };

  // プライバシーポリシーの同意からフォームダイアログへ
  const handlePrivacyAgree = () => {
    setOpenPrivacyDialog(false);
    setOpenDialog(true);
  };

  // お問い合わせ送信の処理
  const handleSubmit = async () => {
    if (!companyName.trim() || !contactName.trim() || !contactEmail.trim()) {
      alert('すべての必須項目を入力してください');
      return;
    }
    if (!isValidEmail(contactEmail.trim())) {
      alert('メールアドレスが正しくありません');
      return;
    }
    if (selectedMedia.length === 0) {
      alert('少なくとも1つの媒体を選択してください');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // 型整合のためのペイロード正規化
      // planから複数のフィールドパターンを試す
      const getFieldValue = (fieldName) => {
        // 複数の場所から値を探す
        const candidates = [
          plan?.[fieldName],
          plan?.formData?.[fieldName],
          plan?.planData?.[fieldName],
          plan?.gptResponse?.[fieldName],
          plan?.gptAdvice?.[fieldName],
          // さらに詳細に探す
          plan?.planA?.[fieldName],
          plan?.planB?.[fieldName],
          plan?.gptAdvice?.planA?.[fieldName],
          plan?.gptAdvice?.planB?.[fieldName]
        ];
        
        // 空でない最初の値を返す
        for (let candidate of candidates) {
          if (candidate && candidate.toString().trim() !== '') {
            return candidate;
          }
        }
        return '';
      };

      // 選択媒体IDが適切に設定されているか確認
      const validMediaIds = selectedMedia
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
      
      // 選択媒体が0件の場合、何らかの警告やログを出力
      if (validMediaIds.length === 0) {
        console.warn('No valid media IDs selected. MediaList:', mediaList);
        console.warn('Selected media raw:', selectedMedia);
      }

      // 選択された媒体の名前を取得
      const selectedMediaNames = selectedMedia
        .map(id => mediaList.find(m => m.id === id)?.name)
        .filter(name => name !== undefined);

      const payload = {
        planName: (plan?.planName || plan?.planSummary || 'AI生成プラン').toString(),
        selectedMediaIds: validMediaIds,
        selectedMediaNames: selectedMediaNames,
        contactEmail: contactEmail.trim(),
        contactName: contactName.trim(),
        companyName: companyName.trim(),
        position: getFieldValue('position').toString(),
        targetHires: getFieldValue('targetHires').toString(),
        advertisingBudget: getFieldValue('advertisingBudget').toString(),
        location: getFieldValue('location').toString()
      };

      const resp = await fetch(
        `${API_URL}/media_selection/send_inquiry_email`,
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(payload)
        }
      );

      if (!resp.ok) {
        let errorDetail = '';
        try {
          const errJson = await resp.json();
          if (errJson && errJson.detail) {
            errorDetail = JSON.stringify(errJson.detail);
          }
        } catch (_) {}
        if (resp.status === 401) {
          throw new Error('認証が必要です。ログインしてから再度お試しください。');
        } else if (resp.status === 403) {
          throw new Error('この操作を実行する権限がありません。');
        } else if (resp.status === 422) {
          throw new Error(`入力内容に誤りがあります: ${errorDetail}`);
        } else {
          throw new Error(`サーバーへの送信に失敗しました (${resp.status})`);
        }
      }

      const result = await resp.json();
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      showError(err, err.message || '送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // 送信完了後の閉じる処理
  const handleCompleteInquiry = () => {
    setOpenDialog(false);
    setSubmitted(false);
    if (onClose) onClose();
  };

  // FAQアイテム
  const faqItems = [
    {
      question: '複数媒体に出すとコストが高くなりませんか？',
      answer: '逆に安くなります。同時出稿によるボリューム割引を適用可能です。複数媒体の掲載で露出を増やしながらもコストを抑えられます。'
    },
    {
      question: '大手代理店との違いは何ですか？',
      answer: '綜合企画は少数精鋭のため、スピーディーで柔軟な対応が可能です。事務的なやり取りではなく、担当者が一貫してきめ細かくフォローします。'
    }
  ];

  return (
    <Paper sx={{ 
      maxWidth: 900, 
      margin: '40px auto',
      p: 4,
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      borderRadius: 2,
      backgroundColor: bg(theme).card
    }}>
      {/* ヘッダー部分 - タイトルを中央に配置するよう修正 */}
      <Box sx={{ position: 'relative', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={onClose}
          sx={{ 
            color: 'text.secondary',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)'
          }}
        >
          プラン選択に戻る
        </Button>
        <Typography 
          variant="h5" 
          component="h1" 
          fontWeight="bold" 
          color="primary"
          sx={{ 
            textAlign: 'center',
            width: '100%'
          }}
        >
          媒体お申し込み
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            width: '100%',
            mt: 1
          }}
        >
          AI人事ご利用企業様向け
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      {/* ユーザーメリット提示セクション */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 4, 
        background: 'linear-gradient(135deg, rgba(102,187,106,0.1), rgba(38,198,218,0.1))',
        borderRadius: 2,
        border: '1px solid rgba(38,198,218,0.1)'
      }}>
        <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          AI人事からのお申し込みで得られる4つのメリット
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', p: 1 }}>
              <MonetizationOnIcon color="primary" sx={{ fontSize: 36, mr: 2 }} />
              <Box>
                <Typography fontWeight="bold">コスト最適化</Typography>
                <Typography variant="body2" color="text.secondary">
                  複数媒体を同時申し込みでディスカウントを適用。予算内で最大効果を実現します。
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', p: 1 }}>
              <SupportAgentIcon color="primary" sx={{ fontSize: 36, mr: 2 }} />
              <Box>
                <Typography fontWeight="bold">手間いらず</Typography>
                <Typography variant="body2" color="text.secondary">
                  修正・調整なども担当者が一括対応。スムーズな掲載をサポートします。
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', p: 1 }}>
              <VerifiedUserIcon color="primary" sx={{ fontSize: 36, mr: 2 }} />
              <Box>
                <Typography fontWeight="bold">少数精鋭のサポート</Typography>
                <Typography variant="body2" color="text.secondary">
                  大手にはない柔軟さとスピード感。担当者が一社一社きめ細かく対応します。
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', p: 1 }}>
              <AutorenewIcon color="primary" sx={{ fontSize: 36, mr: 2 }} />
              <Box>
                <Typography fontWeight="bold">継続的なフォロー</Typography>
                <Typography variant="body2" color="text.secondary">
                  掲載後の応募状況確認や次回提案も無料で実施し、継続的な採用成功を支えます。
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      

      {/* よくある質問 */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.1)',
      }}>
        <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ mr: 1 }} />
          よくある質問
        </Typography>
        
        {faqItems.map((item, index) => (
          <Accordion key={index} elevation={0} sx={{ 
            mb: 1,
            '&:before': { display: 'none' },
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '8px !important',
            overflow: 'hidden'
          }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}
            >
              <Typography fontWeight="medium">{item.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">{item.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
      
      {/* 申し込み後の流れ */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 2,
        border: '1px solid rgba(38,198,218,0.2)',
        bgcolor: 'rgba(38,198,218,0.03)'
      }}>
        <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <AutorenewIcon sx={{ mr: 1 }} />
          申し込み後の流れ
        </Typography>
        
        <Stack spacing={3}>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 2
            }}>1</Box>
            <Box>
              <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <SendIcon sx={{ mr: 1, fontSize: 20 }} />
                フォーム送信
              </Typography>
              <Typography variant="body2" color="text.secondary">
                このページから必要事項を入力して送信するだけ。簡単な操作で申し込みが完了します。
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 2
            }}>2</Box>
            <Box>
              <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                担当より即時ご連絡
              </Typography>
              <Typography variant="body2" color="text.secondary">
                メールまたは電話にて担当者からご連絡。ご希望や詳細についてヒアリングします。
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 2
            }}>3</Box>
            <Box>
              <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <CreateIcon sx={{ mr: 1, fontSize: 20 }} />
                求人情報の作成と媒体入稿
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ヒアリング内容をもとに求人情報をまとめ、媒体へ入稿します。
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 2
            }}>4</Box>
            <Box>
              <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <PublishIcon sx={{ mr: 1, fontSize: 20 }} />
                掲載開始
              </Typography>
              <Typography variant="body2" color="text.secondary">
                担当者が調整を行い、掲載を開始します。
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 2
            }}>5</Box>
            <Box>
              <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 1, fontSize: 20 }} />
                応募数の確認と次回提案
              </Typography>
              <Typography variant="body2" color="text.secondary">
                掲載後の応募状況を確認し、次回の提案も無料で実施。継続的な採用成功をサポートします。
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>
      
      {/* 媒体選択セクション */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>
          申し込み媒体の選択
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <FormControlLabel 
            control={<Checkbox checked={allSelected} onChange={handleSelectAll} color="primary" />}
            label={<Typography fontWeight="bold">すべての媒体を選択</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            最もお得なパッケージ料金が適用されます
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <List sx={{ width: '100%' }}>
          {mediaList.map((media, index) => {
            const mediaId = media?.id || `fallback-${index}`;
            const isSelected = selectedMedia.indexOf(mediaId) !== -1;
            
            return (
                              <ListItem
                  key={`media-item-${mediaId}-${index}`}
                sx={{ 
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: isSelected ? 'rgba(38,198,218,0.05)' : 'transparent',
                  border: isSelected ? '1px solid rgba(38,198,218,0.2)' : '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectMedia(mediaId)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={isSelected ? 'bold' : 'regular'}>
                        {media?.name || '未知の媒体'} 
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        通常価格: {media?.cost || '未設定'}
                      </Typography>
                    </Box>
                  }
                  sx={{ flexGrow: 1 }}
                />
                
                {isSelected && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="caption" fontWeight="bold">
                      選択中
                    </Typography>
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      </Paper>
      
      {/* 申し込みボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SendIcon />}
          onClick={handleOpenPrivacyDialog}
          disabled={selectedMedia.length === 0}
          sx={{
            py: 1.5,
            px: 6,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(38,198,218,0.3)',
            background: 'linear-gradient(135deg, #66bb6a, #26c6da)',
            '&:hover': {
              background: 'linear-gradient(135deg, #43a047, #00acc1)',
            }
          }}
        >
          お申し込み手続きへ進む
        </Button>
      </Box>
      
      {/* プライバシーポリシー同意ダイアログ */}
      <Dialog open={openPrivacyDialog} onClose={() => setOpenPrivacyDialog(false)}>
        <DialogTitle fontWeight="bold">
          お申し込みにあたってのご確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            以下の内容をご確認いただき、同意いただける場合は「同意して申し込む」ボタンを押してください。
          </DialogContentText>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              プライバシーポリシー
            </Typography>
            <Typography variant="body2" paragraph>
              株式会社〇〇（以下「当社」）は、お客様の個人情報保護の重要性を認識し、適切に取り扱います。
              お客様からいただいた個人情報は、お問い合わせへの対応、サービス提供、関連情報のご案内などの目的で利用いたします。
              当社は、法令に基づく場合や業務委託先への提供を除き、お客様の同意なく第三者に個人情報を提供することはありません。
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              サービス利用規約
            </Typography>
            <Typography variant="body2">
              本サービスのご利用にあたり、当社が提供する情報やサービス内容についてご理解いただいたものとみなします。
              お申し込み後のキャンセルは、媒体によって規定が異なりますので、詳細は担当者にお問い合わせください。
              料金は各媒体および申込内容により異なります。正確なお見積りは、お申し込み後に担当者よりご連絡いたします。
            </Typography>
          </Paper>
          <FormControlLabel
            control={<Checkbox checked={privacyAgreed} onChange={(e) => setPrivacyAgreed(e.target.checked)} />}
            label="プライバシーポリシーおよびサービス利用規約に同意します"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setOpenPrivacyDialog(false)}
            color="inherit"
          >
            キャンセル
          </Button>
          <Button 
            onClick={handlePrivacyAgree}
            variant="contained"
            color="primary"
            disabled={!privacyAgreed}
          >
            同意して申し込む
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 申し込み確認ダイアログ */}
      <Dialog open={openDialog} onClose={() => !submitting && setOpenDialog(false)}>
        <DialogTitle fontWeight="bold">
          {submitted ? 'お申し込みが完了しました' : 'お申し込み情報入力'}
        </DialogTitle>
        <DialogContent>
          {submitted ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <DialogContentText>
                お申し込みを受け付けました。3営業日以内でメールまたは電話にて担当者からご連絡いたします。
              </DialogContentText>
            </Box>
          ) : submitting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 3 }}>
                以下の情報をご入力いただくと、AI人事のスタッフがお申し込み手続きをサポートいたします。
              </DialogContentText>
              <TextField
                label="会社名"
                fullWidth
                margin="dense"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
              <TextField
                label="ご担当者名"
                fullWidth
                margin="dense"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
              />
              <TextField
                label="メールアドレス"
                fullWidth
                margin="dense"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
              />
              <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 'bold' }}>
                申し込み媒体:
              </Typography>
              <List dense>
                {selectedMedia.map((id, index) => (
                  <ListItem key={`media-${id}-${index}`} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={mediaList.find(m => m.id === id)?.name || '未知の媒体'} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {submitted ? (
            <Button 
              onClick={handleCompleteInquiry}
              variant="contained"
              color="primary"
              autoFocus
            >
              完了
            </Button>
          ) : submitting ? null : (
            <>
              <Button 
                onClick={() => setOpenDialog(false)}
                color="inherit"
                disabled={submitting}
              >
                キャンセル
              </Button>
              <Button 
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                申し込む
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MediaInquiryPage; 